import React from 'react';
import { Button, Dialog, TextInput } from 'shamrock-clover-ui';
import css from './styles/add-factoring-client.dialog.scss';
import { useDispatch, useSelector } from 'react-redux';
import { addCadenceAccount, getIMUser } from '../../redux/actions/appActions';
import { Spinner } from 'shamrock-clover-ui/dist/clover/components/Spinner/Spinner';
import sessionService from '../../services/session.service';
import { useTranslation } from 'react-i18next';

interface Props {
  open: boolean;
  onCloseClicked: Function;
  onSubmitClicked: Function;
}

export default function AddFactoringClientDialog({ open, onCloseClicked, onSubmitClicked }: Props) {
  const [clientNumber, setClientNumber] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [errorText, setErrorText] = React.useState('');
  const [show, setShow] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(false);
  const imUser = useSelector(getIMUser);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  React.useEffect(() => {
    setShow(true);
    setClientNumber('');
    setPassword('');
    setErrorText('');
    setIsLoading(false);
  }, [open]);

  const handleClientNumberChange = (event) => {
    setClientNumber(event.target.value);
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
  };

  const handleCloseClicked = () => {
    setShow(false);
    onCloseClicked();
  };

  const handleSubmitClicked = async () => {
    const success = await checkPasswordAndAddAccount();
    if (success) {
      setShow(false);
      onSubmitClicked();
    }
  };

  const checkPasswordAndAddAccount = async (): Promise<boolean> => {
    setIsLoading(true);
    const finishAdding = () => {
      dispatch(addCadenceAccount(clientNumber.toUpperCase().trim()));
    };
    if (imUser.accounts.map((acc) => acc.reference_id).includes(clientNumber)) {
      setErrorText(t('already_linked'));
      setIsLoading(false);
      return false;
    }
    if (imUser.user_id.includes('@ryanrts.com')) {
      finishAdding();
      return true;
    } else {
      setErrorText('');
      try {
        await sessionService.createToken(clientNumber, password);
        finishAdding();
        return true;
      } catch (e) {
        setIsLoading(false);
        setErrorText(t('client_number_password_combination_not_found'));
        return false;
      }
    }
  };

  return (
    <div className={css.dialogContainer}>
      <Dialog
        open={open}
        title={t('add_account')}
        onClose={handleCloseClicked}
        content={
          <div style={{ position: 'relative' }}>
            {isLoading && (
              <>
                <div className={css.loadingContainer} />
                <div className={css.loadingSpinner}>
                  <Spinner size="50" />
                </div>
              </>
            )}
            <div>{t('enter_client_number_password')}</div>
            {show && (
              <div className={css.textInputs} data-testid="add-factoring-dialog-fields">
                <TextInput
                  label={t('client_number')}
                  value={clientNumber}
                  onChange={handleClientNumberChange}
                  maxLength={20}
                />
                <TextInput
                  label={t('password')}
                  inputType={'password'}
                  value={password}
                  onChange={handlePasswordChange}
                  error={errorText}
                />
              </div>
            )}
          </div>
        }
        actions={
          <div className={css.actionItems}>
            <Button onClick={handleCloseClicked as any}>{t('cancel')}</Button>
            <Button
              onClick={handleSubmitClicked as any}
              disabled={clientNumber === ''}
              data-testid="add-account-submit-button"
            >
              {t('submit')}
            </Button>
          </div>
        }
      />
    </div>
  );
}
